import * as React from 'react'

import { cn } from '@modules/lib/lib'
import { Card } from '@modules/shadcn/ui/Card/Card'

/**
 * シミュレーション系編集ボードの統一ラッパー。
 *
 * AI Color (現況/加工後画像 + AreaColorSelector) と
 * Area Estimation (積算画像 + 編集パネル) で共通利用する SSOT。
 *
 * デザイン仕様 (AI Color 既存実装 + Figma 13613-44221 準拠):
 *   - 編集モード: `border-lime-200` + `bg-gradient-to-br from-[#86FF80] to-[#FFF370]`
 *     (左上→右下に緑→黄のグラデーション)
 *   - プレビュー: `border-slate-200 bg-[rgba(16,185,129,0.1)]`
 *   - パディング: `p-6`
 *   - レイアウト: 上 `header` 行 / 下 2 カラム (左 `image` 1fr / 右 `sidePanel` 240px)
 *
 * AI Color 側で「画像エリア独自のグラデーション背景」と書かれていた箇所は
 * 本コンポーネントの背景で代用するため重複定義を解消。
 */
interface Props {
  /** デフォルト true。false にすると編集中グラデーションを外し、薄い緑単色になる */
  isEditMode?: boolean
  /** 上部のコントロールバー (タイトル + ボタン群)。省略可 */
  header?: React.ReactNode
  /** 左カラム: 画像 / canvas overlay 等 */
  image: React.ReactNode
  /** 右カラム: コントロールパネル (省略時は image が full width) */
  sidePanel?: React.ReactNode
}

export const SimulationEditorCard: React.FC<Props> = ({
  isEditMode = true,
  header,
  image,
  sidePanel,
}) => (
  <Card
    className={cn(
      'rounded border p-6 shadow-sm',
      isEditMode
        ? 'border-lime-200 bg-gradient-to-br from-[#86FF80] to-[#FFF370]'
        : 'border-slate-200 bg-[rgba(16,185,129,0.1)]',
    )}
  >
    <div className="flex flex-col gap-4">
      {header}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
        <div className="flex flex-1 flex-col gap-4">{image}</div>
        {sidePanel ? (
          <div className="flex w-full flex-col gap-2 lg:relative lg:w-60 lg:shrink-0 lg:self-stretch">
            {sidePanel}
          </div>
        ) : null}
      </div>
    </div>
  </Card>
)
